import { ListItemText, Stack, useTheme } from '@mui/material'
import { StyledSelectTextFieldWithDarkMode, MenuItemWithDarkMode } from 'dark-mode-components'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { capitalizeOnlyFirstLetter } from 'utils'
import React from 'react'
import { COLORS } from 'app-constants'

const Selector = ({
  autoComplete,
  id,
  label,
  defaultOption,
  disabled,
  option,
  options,
  setOption,
  isDarkBackdrop,
  width = '109px',
  renderCustomValue,
  renderCustomOption,
  optionProperties,
  placeholder,
}: {
  autoComplete?: string
  id: string // the id for the selector
  defaultOption?: React.ReactNode // a default option
  disabled?: boolean
  label?: string
  option: any
  options: any[]
  setOption: (option: any) => void
  isDarkBackdrop: boolean // used to conditionally style label text color, sometimes this component is on a light / dark backdrop
  width?: string // width of the selector
  renderCustomValue?: (option: any) => string // used when rendering a custom value
  renderCustomOption?: (option: any) => React.ReactNode // used when rendering a custom option
  optionProperties?: string[] // we want a custom list of fallback properties for rendering the option text
  placeholder?: string // placeholder used when no option is selected
}) => {
  const theme = useTheme()

  const getOptionText = (option: any) => {
    // if option properties is defined, we want to determine which property is available
    if (optionProperties && optionProperties.length > 0) {
      for (const prop of optionProperties) {
        if (option[prop]) {
          return option[prop]
        }
      }
    }

    // if there is no option properties, we want to either return the name property on the option or just the option itself
    return option?.name || option
  }

  return (
    <StyledSelectTextFieldWithDarkMode
      className={label ? 'has-label' : ''}
      hiddenLabel
      autoComplete={autoComplete}
      defaultValue=""
      disabled={disabled}
      id={id}
      isDarkBackdrop={isDarkBackdrop}
      placeholder={placeholder}
      theme={theme}
      label={label || ''}
      renderValue={(option: any) =>
        renderCustomValue ? renderCustomValue(option) : option && capitalizeOnlyFirstLetter(getOptionText(option))
      }
      value={option || ''}
      onChange={(e: any) => setOption(e?.target.value as any)}
      sx={{
        width,
      }}>
      {defaultOption}
      {options.map((o) => {
        let isSelected
        if (!option) {
          isSelected = false
        } else if (typeof o === 'object') {
          isSelected = o.id === option.id
        } else {
          isSelected = o === option
        }

        return (
          <MenuItemWithDarkMode
            theme={theme}
            key={o.id || o}
            value={o}
            isSelected={isSelected}
            sx={{
              color: COLORS.GRAY_0,
            }}>
            {isSelected ? (
              <RadioButtonCheckedIcon
                sx={{
                  fill: COLORS.JELLED_PRIMARY_BLUE,
                }}
              />
            ) : (
              <RadioButtonUncheckedIcon
                sx={{
                  fill: COLORS.GRAY_2,
                }}
              />
            )}
            {renderCustomOption ? (
              <Stack ml={1}>{renderCustomOption(o)}</Stack>
            ) : (
              <ListItemText
                primary={capitalizeOnlyFirstLetter(getOptionText(o))}
                sx={{
                  color: COLORS.GRAY_0,
                  ml: 1,
                }}
              />
            )}
          </MenuItemWithDarkMode>
        )
      })}
    </StyledSelectTextFieldWithDarkMode>
  )
}

export default Selector
